import React, { useState } from 'react';
import { X, Upload, AlertCircle } from 'lucide-react';
import { returnsAPI } from '../../api/returns';
import type { OrderItem } from '../../types';

interface Props {
  orderItem: OrderItem;
  onClose: () => void;
  onSuccess: (rr: any) => void;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_FILES = 5;
const MAX_VIDEO_FILES = 2;

const ReturnRequestModal: React.FC<Props> = ({ orderItem, onClose, onSuccess }) => {
  const [kind, setKind] = useState<'refund' | 'replacement'>('refund');
  const [reason, setReason] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    const next = [...files, ...arr];
    // simple client-side checks; server re-validates
    const images = next.filter(f => f.type.startsWith('image/'));
    const videos = next.filter(f => f.type.startsWith('video/'));
    if (images.length > MAX_IMAGE_FILES) return setError(`Max ${MAX_IMAGE_FILES} images.`);
    if (videos.length > MAX_VIDEO_FILES) return setError(`Max ${MAX_VIDEO_FILES} videos.`);
    const oversized = next.find(f =>
      (f.type.startsWith('image/') && f.size > MAX_IMAGE_BYTES) ||
      (f.type.startsWith('video/') && f.size > MAX_VIDEO_BYTES)
    );
    if (oversized) return setError(`${oversized.name} is too large.`);
    setError('');
    setFiles(next);
  };

  const submit = async () => {
    if (reason.trim().length < 10) {
      setError('Please describe the issue (10+ characters).');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const form = new FormData();
      form.append('order_item_id', String(orderItem.id));
      form.append('kind', kind);
      form.append('reason', reason.trim());
      form.append('quantity', String(quantity));
      files.forEach(f => form.append('evidence', f));

      const rr = await returnsAPI.create(form);
      onSuccess(rr);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to submit return request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white">
          <h2 className="text-base font-semibold">Request Return</h2>
          <button onClick={onClose} className="p-1 -mr-1 text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            {orderItem.product_image && (
              <img src={orderItem.product_image} alt="" className="h-12 w-12 rounded object-cover" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{orderItem.product_name}</p>
              <p className="text-xs text-gray-500">Ordered: {orderItem.quantity}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">I want a</label>
            <div className="grid grid-cols-2 gap-2">
              {(['replacement', 'refund'] as const).map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`px-3 py-2 text-sm rounded-lg border ${
                    kind === k
                      ? 'border-primary-600 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {k === 'refund' ? 'Refund' : 'Replacement'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              min={1}
              max={orderItem.quantity}
              value={quantity}
              onChange={e => setQuantity(Math.max(1, Math.min(orderItem.quantity, Number(e.target.value))))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">What's wrong?</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Describe the issue with the item…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Photos / videos
            </label>
            <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50">
              <Upload className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600">Add files</span>
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={e => addFiles(e.target.files)}
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 text-xs text-gray-600 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 p-4 border-t sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnRequestModal;